import * as amplitude from '@amplitude/analytics-browser';
import { getCampaignContext } from '#/utils/campaignContext';

let isInitialized = false;

declare global {
  interface Window {
    FlutterWebViewBridge?: {
      postMessage: (message: string) => void;
    };
  }
}

export function initAnalytics(): void {
  if (isInitialized) return;

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) {
    console.warn(
      '[analytics] VITE_AMPLITUDE_API_KEY is not set. Skipping init.'
    );
    return;
  }

  const serverUrl = import.meta.env.VITE_AMPLITUDE_SERVER_URL;

  amplitude.init(apiKey, {
    defaultTracking: false,
    ...(serverUrl ? { serverUrl } : {}),
  });

  isInitialized = true;
}

function isAppWebView(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    !!window.FlutterWebViewBridge &&
    typeof window.FlutterWebViewBridge.postMessage === 'function'
  );
}

function getAppType(): 'ios' | 'android' | 'web' {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'web';
}

/**
 * 본체 인증 토큰 저장 시 localStorage에 박히는 CURRENT_USER에서 userId 추출.
 * gov-benefit 자체에는 로그인 플로우가 없지만, 본체 앱 WebView 등 외부 서비스에서
 * 토큰을 박아두고 진입하는 경우가 있어 폴백으로 읽는다. 없으면 키 자체 생략.
 */
function getUserIdProp(): { user_id: string } | Record<string, never> {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem('CURRENT_USER');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { userId?: unknown };
    if (typeof parsed.userId !== 'string' || parsed.userId.length === 0) {
      return {};
    }
    return { user_id: parsed.userId };
  } catch {
    return {};
  }
}

function getCommonEventProperties(): Record<string, unknown> {
  return {
    service_code: 'GOV_BENEFIT',
    is_app: isAppWebView() ? 1 : 0,
    app_type: getAppType(),
    uri: typeof window !== 'undefined' ? window.location.href : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ...getUserIdProp(),
    ...getCampaignContext(),
  };
}

export function track(
  eventName: string,
  eventProperties?: Record<string, unknown>
): void {
  if (!isInitialized) return;
  amplitude.track(eventName, {
    ...getCommonEventProperties(),
    ...eventProperties,
  });
}
